import QueryString from "qs";
import { Overwrite } from "../WSUtils/TypeScriptUtils";
import { UserInfo } from "../WSUtils/UserInfo";
import { lp } from "../WSUtils/logPrefix";
import { withValueVersion } from "../WSUtils/withValueVersion";
import { sentryAssertData } from "../WSUtils/wsSentry";
import { BackendResponse } from "./BackendResponse";
import {
  CODE_NOT_LOGGED_IN_NEXT,
  CODE_NOT_LOGGED_IN_VISMA,
} from "./CUSTOM_STATUS_CODES";
import { getNextApiUrl } from "./getNextApiUrl";

export interface NextApiParams {
  userInfo: UserInfo;
  showSentryDialogOnError?: boolean;
  method?: "POST" | "GET" | "DELETE";
  mapSentryData?: (data: BackendResponse) => BackendResponse;
  [x: string]: any;
}

export interface NextApiHelperParams extends NextApiParams {
  path?: string;
  alreadyAttemptedToGetNewToken?: boolean;
  getData?: () => Promise<any>;
  formData?: FormData;
  headers?: { [key: string]: string };
}

export const getNextApiHeaders = (args: NextApiHelperParams) => {
  const headers = new Headers();

  //const userToken = args.userInfo.userToken;
  const idConcern = args.userInfo.idConcern;

  // const authorization = userToken
  //   ? `bearer ${userToken?.parsed?.access_token}`
  //   : null;

  //headers.append("idCandidate", userToken?.idCandidate?.toString());
  headers.append("idConcern", idConcern?.toString());
  //headers.append("tokenHash", userToken?.hash);
  //headers.append("Authorization", authorization);
  headers.append("Content-Type", "application/json");

  if (args.headers) {
    Object.entries(args.headers).forEach(([key, value]) => {
      if (!value) {
        headers.delete(key);
      } else {
        headers.set(key, value);
      }
    });
  }

  return headers;
};

export const getNextApiRequestBody = (args: NextApiHelperParams) => {
  const {
    userToken,
    path,
    showSentryDialogOnError,
    mapSentryData,
    userInfo,
    method,
    ...rest
  } = args;

  if (method !== "POST") return undefined;

  return rest;
};

export const getNextApiRequestParams = (args: NextApiHelperParams) => {
  const {
    userToken,
    path,
    showSentryDialogOnError,
    mapSentryData,
    userInfo,
    method,
    ...rest
  } = args;

  if (method == "POST") return ""; //changed from !=="GET" to only exclude POST so DELETE requests also gets qs

  return "?" + QueryString.stringify(rest);
};

export const getNextApiProps = (args: NextApiHelperParams) => {
  return {
    url: getNextApiUrl(args.path),
    headers: getNextApiHeaders(args),
    requestBody: getNextApiRequestBody(args),
    requestParams: getNextApiRequestParams(args),
  };
};

export type NextApiMethod<P, R> = (
  args: Overwrite<NextApiParams, P>
) => Promise<R>;

export type DefaultNextApiFetch<R> = (args: NextApiHelperParams) => Promise<R>;

export async function defaultNextApiFetch<R extends BackendResponse>(
  _args: NextApiHelperParams
): Promise<R> {
  const { method = "POST" } = _args;
  const args = { ..._args, method };

  const f = async (): Promise<BackendResponse> => {
    try {
      const defaultGetData = async (): Promise<BackendResponse> => {
        const { headers, url, requestBody, requestParams } =
          getNextApiProps(args);

        let body: any = args.formData;
        if (!body) {
          body = requestBody ? JSON.stringify(requestBody) : undefined;
        }

        const result = await fetch(url + requestParams, {
          method,
          body,
          headers,
        });

        if (!result.ok) {
          let _result;

          try {
            _result = await result.text();
          } catch (err) {}

          return {
            ok: false,
            statusCode: result.status,
            message: `[nextApiHelper-0] ${result.statusText}`.trim(),
            elapsedMilliseconds: null,
            valueVersion: null,
            result: _result,
          };
        }

        try {
          const json = await result.json();

          let response: any = {
            ok: true,
            statusCode: result.status,
            message: result.statusText,
            elapsedMilliseconds: null,
            valueVersion: null,
          };

          if (typeof json === "object" && !Array.isArray(json)) {
            response = {
              ...response,
              ...json,
            };
          } else {
            response = {
              ...response,
              result: json,
            };
          }

          return response;
        } catch (err) {
          console.error(lp(), err);
          return {
            ok: false,
            statusCode: 1500,
            message: `[nextApiHelper-1] ${err?.message}`.trim(),
            result: err,
            elapsedMilliseconds: null,
            valueVersion: null,
          };
        }
      };

      const getData = args.getData || defaultGetData;

      const data = await getData();

      const assumeLoggedOut =
        !args.path.startsWith("/auth") && data?.statusCode === 401;

      if (assumeLoggedOut) {
        let statusCode = CODE_NOT_LOGGED_IN_VISMA;

        if (data?.result === "NO NEXT TOKEN") {
          statusCode = CODE_NOT_LOGGED_IN_NEXT;
        }

        // LocContext.GENERAL
        return {
          ok: false,
          statusCode,
          message:
            "[nextApiHelper-2] " +
            args.userInfo.locStore
              .id("f8310ad244b9")
              .loc("Du er ikke innlogget"),
          elapsedMilliseconds: null,
          valueVersion: null,
          result: data?.result,
        };
      }

      return data;
    } catch (err) {
      console.error(lp(), err);
      return {
        ok: false,
        statusCode: 1500,
        message: `[nextApiHelper-3] ${err?.message}`.trim(),
        result: err,
        elapsedMilliseconds: null,
        valueVersion: null,
      };
    }
  };

  const result = await f();

  if (args.showSentryDialogOnError) {
    sentryAssertData(result, {
      userInfo: args.userInfo,
      mapSentryData: args.mapSentryData,
    });
  }

  return result as any;
}

export interface IWithNextApiOptions<
  R extends BackendResponse,
  P extends NextApiParams = NextApiParams
> {
  f?: (args: P, defaultFetch: DefaultNextApiFetch<R>) => Promise<R>;
  method?: "POST" | "GET" | "DELETE";
}

export function withNextApi<
  R extends BackendResponse,
  P extends NextApiParams = NextApiParams
>(path: string, options?: IWithNextApiOptions<R, P>) {
  let { f, method = "POST" } = options || {};

  if (!f) {
    f = (args, f) => f(args);
  }

  return withValueVersion((args: P) =>
    f({ path, ...args }, (args) => defaultNextApiFetch({ ...args, method }))
  );
}
